import { generateKeywordsQueryForOr } from '@smarttransit/common'
import { findTransportationOwners } from '../../services/transportation-owners-service'
import { addAlert } from '../../utilities/helpers'

export default {
  name: 'transportation-owner-autocomplete',
  props: {
    value: {
      type: [Object, String, Number],
      default: null
    },
    label: {
      type: String,
      default: 'Search account owner'
    },
    placeholder: {
      type: String,
      default: 'Type company or contact'
    },
    disabled: {
      type: Boolean,
      default: false
    },
    clearable: {
      type: Boolean,
      default: true
    },
    searchDelay: {
      type: Number,
      default: 800
    },
    restrictToCurrentUser: {
      type: Boolean,
      default: false
    },
    signedInUser: {
      type: Object,
      default: null
    },
    returnObject: {
      type: Boolean,
      default: true
    }
  },
  data () {
    return {
      selectedOwner: null,
      owners: [],
      isLoading: false,
      searchKey: '',
      searchHandle: null
    }
  },
  computed: {
    allowedOwnerIds () {
      if (!this.restrictToCurrentUser) {
        return []
      }

      const ownerUsers = this.signedInUser && Array.isArray(this.signedInUser.transportationOwnerUsers)
        ? this.signedInUser.transportationOwnerUsers
        : []

      return ownerUsers
        .map(function (item) { return item ? item.transportationOwnerId : null })
        .filter(function (value) { return !!value })
    }
  },
  watch: {
    value: {
      immediate: true,
      async handler (newValue) {
        await this.syncValue(newValue)
      }
    },
    selectedOwner (newValue) {
      if (this.returnObject) {
        this.$emit('input', newValue || null)
        return
      }

      this.$emit('input', newValue && newValue.id ? newValue.id : null)
    },
    searchKey (newValue) {
      this.runSearch(newValue)
    },
    allowedOwnerIds () {
      this.runSearch(this.searchKey)
    }
  },
  mounted () {
    this.runSearch('')
  },
  beforeDestroy () {
    clearTimeout(this.searchHandle)
  },
  methods: {
    ownerIdFromValue (value) {
      if (!value) {
        return null
      }

      if (typeof value === 'object' && value.id) {
        return value.id
      }

      return value
    },
    async syncValue (newValue) {
      if (!newValue) {
        this.selectedOwner = null
        return
      }

      if (typeof newValue === 'object' && newValue.id) {
        this.selectedOwner = newValue
        this.addOwnerIfMissing(newValue)
        return
      }

      await this.loadOwnerById(newValue)
    },
    addOwnerIfMissing (owner) {
      if (!owner || !owner.id) {
        return
      }

      const exists = this.owners.some((item) => String(item.id) === String(owner.id))
      if (!exists) {
        this.owners = [owner].concat(this.owners)
      }
    },
    async loadOwnerById (ownerId) {
      const normalizedId = this.ownerIdFromValue(ownerId)
      if (!normalizedId) {
        this.selectedOwner = null
        return
      }

      try {
        const owners = await findTransportationOwners({
          where: { id: normalizedId },
          fields: { id: true, companyName: true, contactName: true, contactEmail: true, contactPhone: true },
          limit: 1
        })
        const owner = Array.isArray(owners) && owners.length ? owners[0] : null
        this.selectedOwner = owner
        this.addOwnerIfMissing(owner)
      } catch (err) {
        this.selectedOwner = null
      }
    },
    buildSearchFilter (keywords) {
      const trimmedKeywords = String(keywords || '').trim()
      const where = {}
      const andConditions = []

      if (this.allowedOwnerIds.length) {
        andConditions.push({ id: { inq: this.allowedOwnerIds } })
      }

      if (trimmedKeywords) {
        const keywordFilters = generateKeywordsQueryForOr(trimmedKeywords, ['companyName', 'contactName', 'contactEmail', 'contactPhone'])
        if (keywordFilters && keywordFilters.length) {
          andConditions.push({ or: keywordFilters })
        }
      }

      if (andConditions.length === 1) {
        return andConditions[0]
      }

      if (andConditions.length > 1) {
        where.and = andConditions
      }

      return where
    },
    runSearch (keywords) {
      clearTimeout(this.searchHandle)
      this.searchHandle = setTimeout(async () => {
        await this.searchOwners(keywords)
      }, this.searchDelay || 800)
    },
    async searchOwners (keywords) {
      this.isLoading = true
      try {
        const where = this.buildSearchFilter(keywords)
        const owners = await findTransportationOwners({
          where: where,
          fields: { id: true, companyName: true, contactName: true, contactEmail: true, contactPhone: true },
          order: 'companyName ASC',
          limit: 20
        })
        this.owners = Array.isArray(owners) ? owners : []
        this.addOwnerIfMissing(this.selectedOwner)
      } catch (err) {
        addAlert({
          message: err && err.message ? err.message : 'Error searching account owners',
          type: 'error'
        })
      } finally {
        this.isLoading = false
      }
    }
  }
}
